// Two armies (blue = player, red = enemy) that spawn at opposite ends, pathfind to the other
// team's end, and brawl with fists. Two unit kinds share one combat/movement brain:
//   - LITTLE PEOPLE: cheap, many. One InstancedMesh per team of the static char.obj — no gait.
//   - GIANTS: few, big (72 studs). The procedural rig + foot-planting gait.
// Everything (speed, reach, separation, spawn spread) derives from each fighter's height, and
// the giant's rig-frame velocity = studSpeed/rigScale stays a natural jog because SPEED also
// scales with height — so size and leg cadence never desync.

import * as THREE from "three";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader.js";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { TEAM_BLUE, TEAM_RED } from "../config";
import { UNITS_BAND } from "../world/scene";
import { buildNav, findPath, smoothPath, routeVia, laneVias, type NavGraph, type Pt } from "../systems/pathfind";
import { sfx } from "../engine/sfx";
import { Projectiles, type Combatant, type CombatSide, type DamageType } from "../systems/projectiles";
import { balance } from "../systems/balance";
import type { HealthBars } from "../systems/healthbars";
import type { UnitBank } from "../systems/economy";
import { pickRole, MELEE_ROLE, type SoldierRole } from "./roles";
import type { GeneratedLevel } from "../map/grammar";
import { Character, DEFAULT_BODY } from "./char/character";
import { Gait } from "./char/gait";
import type { Controller, Scene3D } from "./char/deps";

// ---- unit sizes + counts ----
const LITTLE_HEIGHT = 23; // studs — the char-file's native size
const GIANT_HEIGHT = 72; // studs — the "before" NPC size, now the rigged giants
const LITTLE_MAX = 80; // per team (instanced; cheap)
const GIANT_POOL_MAX = 12; // giant rig pool capacity per team; the giantMax slider caps active usage
const LITTLE_SPAWN = 0.5; // seconds between little spawns per team
const GIANT_SPAWN = 4.5; // seconds between giant spawns per team

// ---- per-height derived (bigger = faster + longer reach; keeps rig cadence constant) ----
const speedOf = (h: number): number => h * 2.2;
const reachOf = (h: number): number => h * 0.85; // contact/attack distance
const sepOf = (h: number): number => h * 0.65;
const arriveOf = (h: number): number => h * 0.8;
const acquireOf = (h: number): number => h * 8;
// Frontline spread: SAME-team units repel WIDE (fill the corridor into a line instead of a scrum
// ball) while ENEMIES repel only tightly (< punch reach) so they can still interpenetrate to melee.
// Radius keys off the SMALLER unit's height so a little isn't flung far off a friendly giant.
const SAME_SEP = 1.7; // same-team separation radius vs min(height)
const CROSS_SEP = 0.4; // enemy separation radius vs min(height) (< reachOf*0.7 so punches land)
const SAME_STIFF = 8; // same-team spring stiffness (dt-scaled + speed-capped below)
const CROSS_STIFF = 5;
const knockOf = (h: number): number => h * 0.2;
const jitterOf = (h: number): number => h * 0.85;

// ---- shared combat timings ----
const ATTACK_INTERVAL = 0.85;
const PUNCH_DUR = 0.34;
const PUNCH_DAMAGE = 26;
const DEATH_DUR = 0.7;
const UNSTICK_T = 1.0;
const LOS_STEP = 45; // map-relative
const LOS_CLIFF = 110; // map-relative (elevation step is now 200)

const angTo = (target: number, cur: number): number => {
  let d = target - cur;
  while (d > Math.PI) d -= Math.PI * 2;
  while (d < -Math.PI) d += Math.PI * 2;
  return d;
};

function newDriver(): Controller {
  return {
    velX: 0, velZ: 0, vy: 0, speed2d: 0,
    grounded: true, groundExactHeight: 0,
    turnRate: 0, headingErr: 0,
    landImpact: 0, hopParity: 0, bhop: 0,
    aiming: false, aimPitch: 0, lookYaw: 0, lookPitch: 0,
    crouchAmt: 0, proneAmt: 0, slideAmt: 0, diveAmt: 0, jumpCrouch: 0,
  };
}

function teamSkin(color: THREE.Color): THREE.Texture {
  const c = document.createElement("canvas");
  c.width = c.height = 16;
  const ctx = c.getContext("2d")!;
  ctx.fillStyle = "#" + color.getHexString();
  ctx.fillRect(0, 0, 16, 16);
  const t = new THREE.CanvasTexture(c);
  t.magFilter = THREE.NearestFilter;
  t.minFilter = THREE.NearestFilter;
  t.generateMipmaps = false;
  t.colorSpace = THREE.SRGBColorSpace;
  t.needsUpdate = true;
  return t;
}

interface GiantSlot {
  ch: Character;
  active: boolean;
}

interface Fighter {
  kind: "little" | "giant";
  team: 0 | 1;
  h: number; // height (studs)
  hitR: number; // hit radius for bullets (studs)
  pos: THREE.Vector3; // studs
  vel: THREE.Vector3; // studs/sec (xz), net movement last frame
  px: number; // prev-frame pos snapshot (for vel)
  pz: number;
  path: Pt[];
  wi: number;
  yaw: number;
  speed: number;
  reach: number;
  sepR: number;
  arrive: number;
  acquire: number;
  knock: number;
  // combat
  hp: number;
  maxHp: number; // spawn health (for the giant health bar fraction)
  target: Combatant | null; // may be an enemy fighter OR (for ranged roles) an enemy tank
  punchTarget: Fighter | null; // melee only ever punches fighters
  atkCd: number;
  acqCd: number;
  punchT: number;
  pendingHit: boolean;
  dead: boolean;
  deathT: number;
  noMoveT: number;
  footT: number; // giant footstep cadence timer
  walkPhase: number; // little-people march animation phase (advances with speed)
  role: SoldierRole; // combat role (melee brawler, rifleman, bazooka, mortarman, ...)
  // giant rig (only for kind === "giant")
  gslot: GiantSlot | null;
  gait: Gait | null;
  ctrl: Controller | null;
  prevRig: THREE.Vector3;
}

export class Army implements CombatSide {
  private littleGroup = new THREE.Group(); // stud space
  private giantGroup = new THREE.Group(); // rig-unit space, scaled to studs
  private nav: NavGraph | null = null;
  private fighters: Fighter[] = [];
  private projectiles: Projectiles | null = null;
  private healthBars: HealthBars | null = null;

  private littleBlue: THREE.InstancedMesh | null = null;
  private littleRed: THREE.InstancedMesh | null = null;
  private weaponMesh: THREE.InstancedMesh | null = null; // held-weapon props (one box, per-instance tint/scale)
  private giantPoolBlue: GiantSlot[] = [];
  private giantPoolRed: GiantSlot[] = [];

  private littleTimer = 0;
  private giantTimer = 0;
  private ready = false;
  private giantRigScale = 37;
  private gaitWorld: Scene3D;
  private laneCursor: [number, number] = [0, 0]; // per-team spawn counter for round-robin lane spread

  private _e = new THREE.Euler(0, 0, 0, "YXZ");
  private _q = new THREE.Quaternion();
  private _m = new THREE.Matrix4();
  private _s = new THREE.Vector3(1, 1, 1);
  private _p = new THREE.Vector3();
  private _ws = new THREE.Vector3(); // weapon-prop scale
  private _wc = new THREE.Color(); // weapon-prop tint

  // giant frustum-cull: their SkinnedMeshes are frustumCulled=false (bind-pose bounds are wrong once
  // posed), so three.js always draws them. We gate the whole rig on a padded sphere test against the
  // camera frustum — skipping ~6 skinned color + shadow draws per off-screen giant. Littles (one
  // instanced draw) and tanks (frustumCulled=true) don't need this.
  private camera: THREE.Camera | null = null;
  private _frustum = new THREE.Frustum();
  private _projScreen = new THREE.Matrix4();
  private _cullSphere = new THREE.Sphere(new THREE.Vector3(), GIANT_HEIGHT + 110); // body + short sun shadow
  private _frustumOn = false;

  constructor(scene: THREE.Scene) {
    this.littleGroup.name = "littlePeople";
    this.giantGroup.name = "giants";
    scene.add(this.littleGroup, this.giantGroup);
    this.gaitWorld = {
      groundBelow: (x, z) => {
        if (!this.nav) return 0;
        const hh = this.nav.heightAt(x * this.giantRigScale, z * this.giantRigScale);
        return hh == null ? null : hh / this.giantRigScale;
      },
    };
  }

  async load(): Promise<void> {
    const blueSkin = teamSkin(TEAM_BLUE);
    const redSkin = teamSkin(TEAM_RED);

    // ---- little people: static char.obj, one InstancedMesh per team ----
    const obj = await new OBJLoader().loadAsync("/char.obj");
    const geos: THREE.BufferGeometry[] = [];
    obj.traverse((o) => {
      if (!(o instanceof THREE.Mesh)) return;
      let g = o.geometry.clone();
      if (g.index) g = g.toNonIndexed();
      for (const name of Object.keys(g.attributes)) if (name !== "position") g.deleteAttribute(name);
      geos.push(g);
    });
    const merged = geos.length > 1 ? mergeGeometries(geos, false) : geos[0];
    merged.computeBoundingBox();
    const bb = merged.boundingBox!;
    const nh = Math.max(bb.max.y - bb.min.y, 1e-3);
    merged.translate(-(bb.min.x + bb.max.x) / 2, -bb.min.y, -(bb.min.z + bb.max.z) / 2);
    merged.scale(LITTLE_HEIGHT / nh, LITTLE_HEIGHT / nh, LITTLE_HEIGHT / nh);
    merged.computeVertexNormals();
    const litMat = (color: THREE.Color): THREE.MeshStandardMaterial =>
      new THREE.MeshStandardMaterial({ color, roughness: 0.8, flatShading: true });
    this.littleBlue = new THREE.InstancedMesh(merged, litMat(TEAM_BLUE), LITTLE_MAX);
    this.littleRed = new THREE.InstancedMesh(merged, litMat(TEAM_RED), LITTLE_MAX);
    for (const im of [this.littleBlue, this.littleRed]) {
      im.frustumCulled = false;
      im.castShadow = true;
      im.count = 0;
      im.renderOrder = UNITS_BAND; // perf: banded so all unit draws time as one "units" channel
      this.littleGroup.add(im);
    }
    // held-weapon props: one box mesh, per-instance scale + tint distinguishes the roles
    const wGeo = new THREE.BoxGeometry(1, 1, 1);
    const wMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.7, flatShading: true });
    this.weaponMesh = new THREE.InstancedMesh(wGeo, wMat, LITTLE_MAX * 2);
    this.weaponMesh.frustumCulled = false;
    this.weaponMesh.castShadow = true;
    this.weaponMesh.count = 0;
    this.weaponMesh.renderOrder = UNITS_BAND;
    this.littleGroup.add(this.weaponMesh);

    // ---- giants: rig pool, run in rig units inside the scaled giantGroup ----
    const mkGiant = (skin: THREE.Texture): GiantSlot => {
      const ch = new Character(skin, DEFAULT_BODY);
      ch.setOverlay("off");
      ch.root.traverse((o) => { if ((o as THREE.Mesh).isMesh) o.renderOrder = UNITS_BAND; }); // perf: tag every giant skinned/head mesh into the "units" band
      ch.root.visible = false;
      this.giantGroup.add(ch.root);
      return { ch, active: false };
    };
    for (let i = 0; i < GIANT_POOL_MAX; i++) {
      this.giantPoolBlue.push(mkGiant(blueSkin));
      this.giantPoolRed.push(mkGiant(redSkin));
    }
    this.giantRigScale = GIANT_HEIGHT / this.giantPoolBlue[0].ch.height;
    this.giantGroup.scale.setScalar(this.giantRigScale);

    this.ready = true;
  }

  setVisible(v: boolean): void {
    this.littleGroup.visible = v;
    this.giantGroup.visible = v;
  }

  /** The unit render groups — handed to the Scene so the bloom-occluder + water-reflection re-renders can SKIP the
   *  costly skinned units (they barely occlude/reflect). */
  get renderGroups(): THREE.Object3D[] { return [this.littleGroup, this.giantGroup]; }

  private banks: UnitBank[] | null = null; // shared per-team UP pools (index by team)
  autoSpawn = true; // false = units come ONLY from factory assemblers (see deploy) — base-building mode

  /** Manufacture one unit for a team at its pad — called by a factory assembler. Bypasses the UP gate
   *  (the factory already paid in orbital materials) and forces the free grunt role. */
  deploy(team: 0 | 1, kind: "little" | "giant" = "little"): void {
    this.spawn(team, kind, true);
  }

  /** Share the per-team Unit-Point banks (soldiers + giants spend from them; free grunts don't). */
  setBanks(banks: UnitBank[]): void {
    this.banks = banks;
  }

  setProjectiles(p: Projectiles): void {
    this.projectiles = p;
  }

  setHealthBars(hb: HealthBars): void {
    this.healthBars = hb;
  }

  /** Live fighters, for targeting + hit-testing (CombatSide + tank acquire). */
  targets(): Combatant[] {
    return this.fighters;
  }
  units(): readonly Combatant[] {
    return this.fighters;
  }
  damage(u: Combatant, dmg: number, type: DamageType): void {
    // giants especially shrug off explosions (little-people take everything at face value)
    if ((u as Fighter).kind === "giant" && type === "explosion") dmg *= balance.giantBlastDmg;
    this.damageFighter(u, dmg);
  }

  /** Apply damage to a fighter; triggers its death animation when it drops. */
  damageFighter(c: Combatant, dmg: number): void {
    const f = c as Fighter;
    if (f.dead) return;
    f.hp -= dmg;
    if (f.hp <= 0) {
      f.dead = true;
      f.deathT = DEATH_DUR;
      f.target = null;
      f.punchTarget = null;
      sfx.play(f.h >= GIANT_HEIGHT ? "ouch" : "death", f.pos);
    }
  }

  setLevel(lvl: GeneratedLevel, navOverride?: NavGraph): void {
    for (const f of this.fighters) {
      if (f.gslot) {
        f.gslot.active = false;
        f.gslot.ch.root.visible = false;
      }
    }
    this.fighters = [];
    this.littleTimer = 0;
    this.giantTimer = 0;
    this.nav = navOverride ?? buildNav(lvl); // terra battles hand in their prebuilt open-field nav
    if (this.littleBlue) this.littleBlue.count = 0;
    if (this.littleRed) this.littleRed.count = 0;
  }

  private losWalkable(ax: number, az: number, bx: number, bz: number): boolean {
    const nav = this.nav!;
    const dx = bx - ax;
    const dz = bz - az;
    const steps = Math.max(1, Math.ceil(Math.hypot(dx, dz) / LOS_STEP));
    // TERRA (smooth ground): a true SIGHT-LINE test — the ground profile must stay under the
    // interpolated eye line, so hill crests are real cover / high ground. (The tile heuristic
    // below never blocks on rolling slopes → riflemen would fire into hillsides forever.)
    const shot = nav.shotHeightAt;
    if (shot) {
      const ha = shot(ax, az);
      const hb = shot(bx, bz);
      if (ha == null || hb == null) return false;
      const eyeA = ha + 22, eyeB = hb + 22, dEye = eyeB - eyeA;
      for (let s = 1; s < steps; s++) {
        const t = s / steps;
        const h = shot(ax + dx * t, az + dz * t);
        if (h == null || h > eyeA + dEye * t) return false;
      }
      return true;
    }
    let prev = nav.heightAt(ax, az);
    if (prev == null) return false;
    for (let s = 1; s <= steps; s++) {
      const h = nav.heightAt(ax + (dx * s) / steps, az + (dz * s) / steps);
      if (h == null || Math.abs(h - prev) > LOS_CLIFF) return false;
      prev = h;
    }
    return true;
  }

  private targetSides: CombatSide[] = []; // extra enemy sources (tanks) ranged roles may target

  /** Register a side (e.g. the tanks) that ranged soldiers may also target + shoot. */
  addTargetSide(side: CombatSide): void {
    this.targetSides.push(side);
  }

  private acquire(f: Fighter): Combatant | null {
    const R = f.role.range;
    const wantLOS = f.role.attack !== "arc"; // melee + direct fire need a clear line; lobs don't
    let best: Combatant | null = null;
    let bd = R * R;
    const consider = (c: Combatant): void => {
      const d = (c.pos.x - f.pos.x) ** 2 + (c.pos.z - f.pos.z) ** 2;
      if (d < bd && (!wantLOS || this.losWalkable(f.pos.x, f.pos.z, c.pos.x, c.pos.z))) {
        bd = d;
        best = c;
      }
    };
    for (const g of this.fighters) {
      if (g.team === f.team || g.dead) continue;
      consider(g);
    }
    // ranged soldiers can also engage enemy tanks (melee fists can't hurt armor)
    if (f.role.attack !== "melee") for (const side of this.targetSides) {
      for (const u of side.units()) {
        if (u.team === f.team || u.dead || u.active === false) continue;
        consider(u);
      }
    }
    return best;
  }

  /** A ranged soldier fires its weapon at its target. Returns false if the shot didn't spawn
   *  (shared pool full) so the caller keeps its cooldown and retries — and stays silent. */
  private fireWeapon(f: Fighter): boolean {
    const p = this.projectiles;
    const t = f.target;
    if (!p || !t) return false;
    const role = f.role;
    const dx = t.pos.x - f.pos.x;
    const dz = t.pos.z - f.pos.z;
    const range = Math.hypot(dx, dz) || 1;
    // muzzle: a little forward of the soldier, in the aim direction
    const mx = f.pos.x + (dx / range) * f.h * 0.4;
    const mz = f.pos.z + (dz / range) * f.h * 0.4;
    if (role.attack === "arc") {
      const dur = Math.max(0.9, Math.min(2.6, range / 380));
      const leadK = dur * 0.9;
      const jit = 0.06 * range;
      const tx = t.pos.x + t.vel.x * leadK + (Math.random() * 2 - 1) * jit;
      const tz = t.pos.z + t.vel.z * leadK + (Math.random() * 2 - 1) * jit;
      if (!p.fireArc(mx, mz, f.pos.y, tx, tz, f.team, role.damage, role.blastR, role.arcPeak, null)) return false;
      sfx.play("artillery", f.pos);
      return true;
    }
    const leadK = range / role.speed;
    const aimYaw = Math.atan2(t.pos.x + t.vel.x * leadK - mx, t.pos.z + t.vel.z * leadK - mz);
    const fireYaw = aimYaw + (Math.random() * 2 - 1) * 0.05; // small aim scatter
    // short TTL so short-range bullets recycle fast and don't hog the shared pool
    const ttl = Math.min(3.6, (role.range / role.speed) * 1.5 + 0.4);
    if (!p.fireStraight(mx, mz, f.pos.y, Math.sin(fireYaw), Math.cos(fireYaw), role.speed, f.team, role.damage, role.bounces, role.blastR, role.projSize, f.team, null, ttl)) return false;
    sfx.play(role.attack === "rocket" ? "artillery" : "fire", f.pos);
    return true;
  }

  private nudge(f: Fighter, ox: number, oz: number): void {
    const nav = this.nav!;
    if (nav.heightAt(f.pos.x + ox, f.pos.z + oz) != null) {
      f.pos.x += ox;
      f.pos.z += oz;
      return;
    }
    // per-axis slide: keep the along-wall component when the diagonal lands off-walkable, so a
    // separation push into a wall still spreads teammates along it instead of being dropped.
    if (ox !== 0 && nav.heightAt(f.pos.x + ox, f.pos.z) != null) f.pos.x += ox;
    else if (oz !== 0 && nav.heightAt(f.pos.x, f.pos.z + oz) != null) f.pos.z += oz;
  }

  private slideMove(f: Fighter, dx: number, dz: number): number {
    const nav = this.nav!;
    if (nav.heightAt(f.pos.x + dx, f.pos.z + dz) != null) {
      f.pos.x += dx;
      f.pos.z += dz;
      return Math.hypot(dx, dz);
    }
    if (dx !== 0 && nav.heightAt(f.pos.x + dx, f.pos.z) != null) {
      f.pos.x += dx;
      return Math.abs(dx);
    }
    if (dz !== 0 && nav.heightAt(f.pos.x, f.pos.z + dz) != null) {
      f.pos.z += dz;
      return Math.abs(dz);
    }
    return 0;
  }

  private spawn(team: 0 | 1, kind: "little" | "giant", forced = false): void {
    const nav = this.nav;
    if (!nav || !this.ready) return;
    const bank = this.banks ? this.banks[team] : null;
    let gslot: GiantSlot | null = null;
    let role: SoldierRole;
    let cost: number;
    if (kind === "giant") {
      const gpool = team === 0 ? this.giantPoolBlue : this.giantPoolRed;
      if (gpool.filter((s) => s.active).length >= balance.giantMax) return; // rarity cap (per team)
      gslot = gpool.find((s) => !s.active) ?? null;
      if (!gslot) return; // pool exhausted
      role = MELEE_ROLE; // giants stay rigged-punch brawlers
      cost = forced ? 0 : balance.giantCost;
    } else {
      const alive = this.fighters.filter((f) => f.kind === "little" && f.team === team && !f.dead).length;
      if (alive >= LITTLE_MAX) return;
      role = forced ? MELEE_ROLE : pickRole(balance.specialFrac); // factory grunt, or the auto-spawn mix
      cost = forced ? 0 : role.cost;
    }
    // UP gate: cost-0 grunts always spawn (the filler); anything priced must be affordable, else the
    // team spawns nothing and banks the UP (hold-and-bank → bursty specials). Both teams, same rules.
    if (cost > 0 && (!bank || !bank.canAfford(cost))) return;

    const from = team === 0 ? nav.startKeys : nav.coreKeys;
    const to = team === 0 ? nav.coreKeys : nav.startKeys;
    if (!from.length || !to.length) return;
    const fromKey = from[(Math.random() * from.length) | 0];
    const toKey = to[(Math.random() * to.length) | 0];
    // route through this unit's assigned lane at every diamond, so the army fans across the lanes
    const vias = laneVias(nav, team === 0, this.laneCursor[team]++);
    let path = routeVia(nav, fromKey, vias, toKey);
    if (!path) {
      const cellPath = findPath(nav, fromKey, toKey); // fallback: direct route
      if (!cellPath || cellPath.length < 2) return;
      path = smoothPath(nav, cellPath);
    }
    if (path.length < 2) return;
    const p0 = path[0];
    const h = kind === "giant" ? GIANT_HEIGHT : LITTLE_HEIGHT;
    let sx = p0.x + (Math.random() * 2 - 1) * jitterOf(h);
    let sz = p0.z + (Math.random() * 2 - 1) * jitterOf(h);
    if (nav.heightAt(sx, sz) == null) {
      sx = p0.x;
      sz = p0.z;
    }
    const sy = nav.heightAt(sx, sz) ?? p0.y;

    if (cost > 0 && bank) bank.spend(cost); // commit the UP now that the spawn is going through

    if (gslot) {
      gslot.active = true;
      gslot.ch.root.visible = true;
      gslot.ch.root.rotation.order = "YXZ";
      gslot.ch.root.rotation.set(0, 0, 0);
      gslot.ch.setGroundPosition(sx / this.giantRigScale, sy / this.giantRigScale, sz / this.giantRigScale);
    }
    this.fighters.push({
      kind,
      team,
      h,
      role,
      hitR: h * 0.6,
      pos: new THREE.Vector3(sx, sy, sz),
      vel: new THREE.Vector3(),
      px: sx,
      pz: sz,
      path,
      wi: 1,
      yaw: 0,
      speed: speedOf(h),
      reach: reachOf(h),
      sepR: sepOf(h),
      arrive: arriveOf(h),
      acquire: acquireOf(h),
      knock: knockOf(h),
      hp: kind === "giant" ? balance.giantHp : balance.soldierHp,
      maxHp: kind === "giant" ? balance.giantHp : balance.soldierHp,
      target: null,
      punchTarget: null,
      atkCd: Math.random() * 0.4,
      acqCd: Math.random() * 0.3,
      punchT: 0,
      pendingHit: false,
      dead: false,
      deathT: 0,
      noMoveT: 0,
      footT: Math.random() * 0.4,
      walkPhase: Math.random() * Math.PI * 2,
      gslot,
      gait: gslot ? new Gait(gslot.ch, this.gaitWorld) : null,
      ctrl: gslot ? newDriver() : null,
      prevRig: new THREE.Vector3(sx / this.giantRigScale, sy / this.giantRigScale, sz / this.giantRigScale),
    });
  }

  /** Give the army the camera so giants can be frustum-culled (their meshes never self-cull). */
  setCamera(camera: THREE.Camera): void {
    this.camera = camera;
  }

  update(dt: number): void {
    if (!this.ready || !this.nav || !this.littleGroup.visible) return;
    if (dt < 1e-4) return;
    if (dt > 1 / 30) dt = 1 / 30;
    const rot14 = Math.min(1, 14 * dt), rot12 = Math.min(1, 12 * dt); // per-frame turn/settle rates (hoisted)

    // build the view frustum once/frame (world/stud space) to gate giant visibility below
    this._frustumOn = false;
    if (this.camera) {
      const cam = this.camera;
      cam.updateMatrixWorld();
      cam.matrixWorldInverse.copy(cam.matrixWorld).invert();
      this._projScreen.multiplyMatrices(cam.projectionMatrix, cam.matrixWorldInverse);
      this._frustum.setFromProjectionMatrix(this._projScreen);
      this._frustumOn = true;
    }

    // spawn (auto-spawn timers; disabled in base-building mode where factories manufacture units)
    if (this.autoSpawn) {
      this.littleTimer -= dt;
      if (this.littleTimer <= 0) {
        this.littleTimer = LITTLE_SPAWN;
        this.spawn(0, "little");
        this.spawn(1, "little");
      }
      this.giantTimer -= dt;
      if (this.giantTimer <= 0) {
        this.giantTimer = GIANT_SPAWN;
        this.spawn(0, "giant");
        this.spawn(1, "giant");
      }
    }

    // snapshot pos so velocity = net movement across this whole frame (all passes)
    for (const f of this.fighters) {
      f.px = f.pos.x;
      f.pz = f.pos.z;
    }

    const alive: Fighter[] = [];

    // ---- PASS 1: combat + walkable movement ----
    for (const f of this.fighters) {
      if (f.dead) {
        f.deathT -= dt;
        if (f.deathT <= 0) {
          if (f.gslot) {
            f.gslot.active = false;
            f.gslot.ch.root.visible = false;
            f.gslot.ch.root.rotation.x = 0;
          }
          continue;
        }
        alive.push(f);
        continue;
      }

      f.atkCd -= dt;
      if (f.punchT > 0) f.punchT -= dt;

      const wantLOS = f.role.attack !== "arc"; // lobs (grenade/mortar) don't need a clear line
      if (f.target) {
        const dx = f.target.pos.x - f.pos.x;
        const dz = f.target.pos.z - f.pos.z;
        if (
          f.target.dead ||
          f.target.active === false ||
          dx * dx + dz * dz > f.role.range * f.role.range ||
          (wantLOS && !this.losWalkable(f.pos.x, f.pos.z, f.target.pos.x, f.target.pos.z))
        ) {
          f.target = null;
        }
      }
      f.acqCd -= dt;
      if (!f.target && f.acqCd <= 0) {
        f.target = this.acquire(f);
        f.acqCd = 0.2 + Math.random() * 0.15;
      }

      let attacking = false;
      if (f.target) {
        const dx = f.target.pos.x - f.pos.x;
        const dz = f.target.pos.z - f.pos.z;
        const dist = Math.hypot(dx, dz);
        if (f.role.attack === "melee") {
          const reach = (f.reach + (f.target as Fighter).reach) * 0.5;
          if (dist <= reach) {
            attacking = true;
            f.yaw += angTo(Math.atan2(dx, dz), f.yaw) * rot14;
            if (f.atkCd <= 0) {
              f.atkCd = ATTACK_INTERVAL * (0.85 + Math.random() * 0.3);
              f.punchT = PUNCH_DUR;
              f.pendingHit = true;
              f.punchTarget = f.target as Fighter;
            }
          }
        } else if (dist <= f.role.range) {
          // ranged: plant at range and fire (chasing below only runs when still out of range)
          attacking = true;
          f.yaw += angTo(Math.atan2(dx, dz), f.yaw) * rot14;
          if (f.atkCd <= 0 && this.fireWeapon(f)) {
            f.atkCd = f.role.interval * (0.85 + Math.random() * 0.3);
          }
        }
      }

      if (f.pendingHit && f.punchT <= PUNCH_DUR * 0.5) {
        f.pendingHit = false;
        const t = f.punchTarget;
        if (t && !t.dead) {
          const tx = t.pos.x - f.pos.x;
          const tz = t.pos.z - f.pos.z;
          const dd = Math.hypot(tx, tz);
          if (dd <= (f.reach + t.reach) * 0.7) {
            t.hp -= PUNCH_DAMAGE;
            sfx.play("melee", f.pos); // the punch connects
            if (t.hp <= 0) {
              t.dead = true;
              t.deathT = DEATH_DUR;
              t.target = null;
              sfx.play(t.h >= GIANT_HEIGHT ? "ouch" : "death", t.pos); // heavier cry for a felled giant
            } else if (dd > 1e-3) {
              this.nudge(t, (tx / dd) * f.knock, (tz / dd) * f.knock);
            }
          }
        }
      }

      if (!attacking) {
        let tx: number;
        let tz: number;
        const chasing = !!f.target;
        if (f.target) {
          tx = f.target.pos.x;
          tz = f.target.pos.z;
        } else {
          let dxp = f.path[f.wi].x - f.pos.x;
          let dzp = f.path[f.wi].z - f.pos.z;
          let dist = Math.hypot(dxp, dzp);
          while (dist < f.arrive && f.wi < f.path.length - 1) {
            f.wi++;
            dxp = f.path[f.wi].x - f.pos.x;
            dzp = f.path[f.wi].z - f.pos.z;
            dist = Math.hypot(dxp, dzp);
          }
          if (f.wi >= f.path.length - 1 && dist < f.arrive) {
            f.dead = true; // tombstone; marched to the far end
            if (f.gslot) {
              f.gslot.active = false;
              f.gslot.ch.root.visible = false;
            }
            continue;
          }
          tx = f.path[f.wi].x;
          tz = f.path[f.wi].z;
        }
        const dx = tx - f.pos.x;
        const dz = tz - f.pos.z;
        const dist = Math.hypot(dx, dz);
        if (dist > 1e-4) {
          // Melee stops at the mutual reach (so a big unit still closes inside its own trigger);
          // ranged stops ~at its weapon range so it holds a firing standoff instead of charging in.
          const stopReach = chasing && f.target
            ? (f.role.attack === "melee" ? (f.reach + (f.target as Fighter).reach) * 0.5 : f.role.range * 0.9)
            : 0;
          const cap = chasing ? Math.max(0, dist - stopReach) : dist;
          const adv = Math.min(f.speed * dt, cap);
          const moved = adv > 0 ? this.slideMove(f, (dx / dist) * adv, (dz / dist) * adv) : 0;
          f.yaw += angTo(Math.atan2(dx, dz), f.yaw) * rot12;
          if (moved < adv * 0.5 + 0.01 && adv > 0) {
            f.noMoveT += dt;
            if (f.noMoveT > UNSTICK_T) {
              if (chasing) {
                // chasing an UNREACHABLE target (e.g. across a crevasse the eye-line LOS sees over):
                // drop it + brief re-acquire cooldown, resume the march (review finding — melee
                // otherwise stalls at hazard edges forever)
                f.target = null;
                f.acqCd = Math.max(f.acqCd, 1.0);
              } else if (f.wi < f.path.length - 1) {
                f.wi++;
              }
              f.noMoveT = 0;
            }
          } else {
            f.noMoveT = 0;
          }
        }
      } else {
        f.noMoveT = 0;
      }
      alive.push(f);
    }

    // ---- PASS 2: separation (unbunch), walkable-clamped ----
    const n = alive.length;
    for (let i = 0; i < n; i++) {
      const a = alive[i];
      if (a.dead) continue;
      const at = a.team, ah = a.h, asp = a.speed; // loop-invariant (nudge mutates a.pos, not these)
      for (let j = i + 1; j < n; j++) {
        const b = alive[j];
        if (b.dead) continue;
        const dx = b.pos.x - a.pos.x;
        const dz = b.pos.z - a.pos.z;
        const d2 = dx * dx + dz * dz;
        const same = at === b.team;
        const sep = Math.min(ah, b.h) * (same ? SAME_SEP : CROSS_SEP);
        if (d2 < sep * sep && d2 > 1e-3) {
          const d = Math.sqrt(d2);
          const overlap = sep - d;
          // dt-scaled spring, capped at ~1.5x locomotion so it spreads smoothly instead of teleporting
          const cap = (asp + b.speed) * 0.5 * dt * 1.5;
          const push = Math.min(overlap * (same ? SAME_STIFF : CROSS_STIFF) * dt, cap, overlap * 0.5);
          const ux = dx / d;
          const uz = dz / d;
          this.nudge(a, -ux * push, -uz * push);
          this.nudge(b, ux * push, uz * push);
        }
      }
    }

    // ---- PASS 3: settle onto surface + render each kind ----
    let bi = 0;
    let ri = 0;
    let wc = 0; // weapon-prop instance count
    const gInv = 1 / this.giantRigScale;
    for (const f of alive) {
      // dead fighters (still animating) don't settle — skip the wasted nav.heightAt for them
      if (!f.dead) {
        const gy = this.nav.heightAt(f.pos.x, f.pos.z);
        if (gy != null) f.pos.y += (gy - f.pos.y) * rot14;
      }

      if (f.kind === "little") {
        // instance matrix: position (studs) + yaw. A cheap procedural MARCH from the instance
        // matrix alone (no per-limb rig): a vertical bob, a side-to-side waddle roll, and a slight
        // forward lean, all driven by walkPhase (advanced by speed). On death, tip over instead.
        const im = f.team === 0 ? this.littleBlue! : this.littleRed!;
        const idx = f.team === 0 ? bi++ : ri++;
        const spd = Math.hypot(f.vel.x, f.vel.z);
        const walking = !f.dead && spd > 5;
        if (walking) f.walkPhase += Math.min(spd / f.h, 3) * dt * 9; // cadence scales with stride
        const bob = walking ? Math.abs(Math.sin(f.walkPhase)) * f.h * 0.07 : 0;
        const roll = walking ? Math.sin(f.walkPhase) * 0.16 : 0; // waddle
        const lean = walking ? 0.12 : 0; // forward march lean
        const tip = f.dead ? Math.min(1.4, (1 - f.deathT / DEATH_DUR) * 1.4) : lean;
        const sink = f.dead ? -(1 - f.deathT / DEATH_DUR) * f.h * 0.15 : bob;
        this._e.set(tip, f.yaw, roll);
        this._q.setFromEuler(this._e);
        this._p.set(f.pos.x, f.pos.y + sink, f.pos.z);
        this._m.compose(this._p, this._q, this._s);
        im.setMatrixAt(idx, this._m);

        // held weapon prop (a scaled/tinted box pointing where the soldier aims)
        const wv = f.role.weapon;
        if (wv && !f.dead) {
          const fwx = Math.sin(f.yaw);
          const fwz = Math.cos(f.yaw);
          const len = wv.sz * f.h;
          const off = len * 0.5 + f.h * 0.15;
          this._ws.set(wv.sx * f.h, wv.sy * f.h, len);
          this._e.set(-wv.pitch, f.yaw, 0); // Ry(yaw)·Rx(-pitch): barrel points forward, tilted up
          this._q.setFromEuler(this._e);
          this._p.set(f.pos.x + fwx * off, f.pos.y + sink + f.h * 0.5, f.pos.z + fwz * off);
          this._m.compose(this._p, this._q, this._ws);
          this.weaponMesh!.setMatrixAt(wc, this._m);
          this.weaponMesh!.setColorAt(wc, this._wc.setHex(wv.color));
          wc++;
        }
      } else {
        const ch = f.gslot!.ch;
        // cull the whole rig when fully off-screen (its meshes won't self-cull). Gait still runs so
        // the pose is correct the instant it re-enters view — only the GPU draws/shadows are skipped.
        if (this._frustumOn) {
          this._cullSphere.center.set(f.pos.x, f.pos.y + GIANT_HEIGHT * 0.5, f.pos.z);
          ch.root.visible = this._frustum.intersectsSphere(this._cullSphere);
        }
        if (f.dead) {
          const p = Math.min(1, 1 - f.deathT / DEATH_DUR);
          ch.root.position.set(f.pos.x * gInv, f.pos.y * gInv - p * 0.12, f.pos.z * gInv);
          ch.root.rotation.x = p * 1.35;
          continue;
        }
        const rx = f.pos.x * gInv;
        const ry = f.pos.y * gInv;
        const rz = f.pos.z * gInv;
        const vx = (rx - f.prevRig.x) / dt;
        const vz = (rz - f.prevRig.z) / dt;
        f.prevRig.set(rx, ry, rz);
        // giant footfalls: a marching-cadence stomp while striding (only ~10 giants, so cheap)
        f.footT -= dt;
        if (f.footT <= 0 && Math.hypot(f.vel.x, f.vel.z) > 20) {
          sfx.play("footstep", f.pos);
          f.footT = 0.42;
        }
        if (ch.root.visible) this.healthBars?.add(f.pos.x, f.pos.y + 88, f.pos.z, f.hp / f.maxHp);
        ch.root.position.set(rx, ry, rz);
        ch.root.rotation.set(0, f.yaw, 0);
        ch.swing = f.punchT > 0 ? f.punchT / PUNCH_DUR : 0;
        const ctrl = f.ctrl!;
        ctrl.velX = vx;
        ctrl.velZ = vz;
        ctrl.speed2d = Math.hypot(vx, vz);
        ctrl.grounded = true;
        ctrl.groundExactHeight = ry;
        f.gait!.update(dt, ctrl);
        if (f.punchT > 0) {
          const strike = Math.sin((1 - f.punchT / PUNCH_DUR) * Math.PI);
          ch.joints.shoulderR.rotation.x = 1.5 * strike;
          ch.joints.elbowR.rotation.x = -0.9 * (1 - strike);
        }
      }
    }
    this.littleBlue!.count = bi;
    this.littleRed!.count = ri;
    // An InstancedMesh with count 0 still costs a full program bind + uniform upload + draw call in EVERY pass
    // (main + bloom occluder + reflection + shadow) while rasterizing nothing. Hiding it is free and cannot
    // change a pixel. Idle base-building leaves most of these empty. NB: never do this to a mesh carrying a
    // GPU cost probe (e.g. the factory's oreMesh) — the probe rides onBeforeRender and would stop firing.
    this.littleBlue!.visible = bi > 0;
    this.littleRed!.visible = ri > 0;
    this.littleBlue!.instanceMatrix.needsUpdate = true;
    this.littleRed!.instanceMatrix.needsUpdate = true;
    const wm = this.weaponMesh!;
    wm.count = wc;
    wm.visible = wc > 0;
    wm.instanceMatrix.needsUpdate = true;
    if (wm.instanceColor) wm.instanceColor.needsUpdate = true;

    // net per-frame velocity, so tank shells can lead moving fighters
    const inv = 1 / dt;
    for (const f of alive) {
      f.vel.set((f.pos.x - f.px) * inv, 0, (f.pos.z - f.pz) * inv);
    }

    this.fighters = alive;
  }
}
